/**
 * Reading an instant out of a timestamp the API sent.
 *
 * **The API sends UTC and does not say so.** EF Core hands back `DateTime`
 * values with `Kind = Unspecified`, and System.Text.Json writes those with no
 * trailing `Z` — `2026-09-23T19:14:00`. JavaScript reads a string in that shape
 * as *local* time, so every timestamp in this portal has been rendering at the
 * stored number of hours rather than the correct local one: six hours early in
 * Mountain time, five in Central.
 *
 * Found 23 September from the other end. A tester saw a tow's promised arrival
 * as 7:14 PM in one place and 1:14 PM in another, at the same minute, from one
 * field. The mobile app had already grown a private workaround for this in
 * `SentRequestScreen` — appending the `Z` before parsing — which is why one of
 * its two screens was right and the other was not.
 *
 * **Date-only strings are left alone.** `2026-09-23` is already parsed as UTC
 * midnight by the language spec, so there is nothing to correct; appending a
 * `Z` to a value that has no time is at best a no-op and at worst a way to turn
 * a date into the evening before it.
 *
 * The server emitting proper designators is the better fix, and the one to make
 * eventually. It is a wire change that the mobile app reads too, so it does not
 * belong in the same breath as correcting the display.
 */

/** True when the string states its zone: a trailing `Z`, or a `±HH:MM` offset. */
function carriesZone(iso: string): boolean {
  return /[Zz]$/.test(iso) || /[+-]\d{2}:\d{2}$/.test(iso);
}

/** True when there is a time to be wrong about — `2026-09-23` has none. */
function carriesTime(iso: string): boolean {
  return /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(iso);
}

/**
 * A `Date` for an API timestamp, or null when it cannot be read.
 *
 * Null rather than an Invalid Date, because every caller renders the result and
 * "Invalid Date" in a table cell is worse than an em-dash.
 */
export function parseApiInstant(value: string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const needsUtc = carriesTime(value) && !carriesZone(value);
  const at = new Date(needsUtc ? `${value.replace(' ', 'T')}Z` : value);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * An instant as the `YYYY-MM-DDTHH:mm` a `datetime-local` input expects, in the
 * viewer's own zone.
 *
 * The counterpart to parsing, and the half that was missing. The offer dialog
 * pre-filled its arrival field by slicing the raw server string, which put a
 * UTC clock time into an input that means local — so an offer re-saved without
 * touching that field moved the promised arrival later by the size of the
 * offset, every time it was saved.
 */
export function toDateTimeLocalValue(value: string | null | undefined): string {
  const at = parseApiInstant(value);
  if (!at) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `T${pad(at.getHours())}:${pad(at.getMinutes())}`
  );
}
